import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { filter, firstValueFrom } from 'rxjs';
import { UserState } from 'shared-states';
import { ShellState } from '../states';
import { ClassicBasketDeeplink } from './deeplinks/classic-basket.deeplink';
import { NavigationTarget } from './navigation-target';
import { ShellRoutes } from './portal-routes';
import { PortalUrls } from './portal-urls.service';
import { ReturnUrlStore } from './return-url/return-url.store';
import { LandingContext } from './user-journey/landing-rule';
import { UserJourney } from './user-journey/user-journey.service';

/**
 * Decides where an authenticated user goes: the requested deeplink (one to the classic basket leads to the
 * Order Pro basket, see `ClassicBasketDeeplink`), otherwise the User Journey.
 */
@Injectable({ providedIn: 'root' })
export class LandingResolver {
  private readonly store = inject(Store);
  private readonly returnUrls = inject(ReturnUrlStore);
  private readonly userJourney = inject(UserJourney);
  private readonly urls = inject(PortalUrls);
  private readonly classicBasket = inject(ClassicBasketDeeplink);

  async resolve(): Promise<NavigationTarget> {
    const deeplink = this.returnUrls.consume();
    if (deeplink) {
      return this.classicBasket.resolve(deeplink) ?? deeplink;
    }

    // The User Journey depends on the user and the organizations loaded by the session initialization
    await firstValueFrom(this.store.select(ShellState.isInitialized).pipe(filter(Boolean)));
    if (this.store.selectSnapshot(ShellState.getStateError)) {
      return NavigationTarget.internal(ShellRoutes.error);
    }
    return this.userJourney.resolve(this.createContext());
  }

  private createContext(): LandingContext {
    const { user, organizations, selectedOrganization } = this.store.selectSnapshot(UserState.getState);
    return {
      user,
      organizations: organizations ?? [],
      selectedOrganization,
      deviceType: this.store.selectSnapshot(UserState.getDeviceType),
      region: this.urls.region
    };
  }
}
